import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  INSIGHTS_SECTION_KEYS,
  fetchInsightsPage,
  findSection,
} from "../services/contentApi";

const InsightsPageContext = createContext(null);

export function InsightsPageProvider({ children }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchInsightsPage()
      .then((data) => {
        if (!cancelled) {
          setPage(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setPage(null);
        }
        console.error("Error fetching insights page:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sections = useMemo(() => {
    const result = {};
    INSIGHTS_SECTION_KEYS.forEach((key) => {
      result[key] = findSection(page, key);
    });
    if (!result.articles && page?.sections) {
      result.articles =
        page.sections.find(
          (s) =>
            (s.sectionType || s.type || "").toLowerCase() === "articles"
        ) ?? null;
    }
    return result;
  }, [page]);

  const value = useMemo(
    () => ({ page, sections, loading, error }),
    [page, sections, loading, error]
  );

  return (
    <InsightsPageContext.Provider value={value}>
      {children}
    </InsightsPageContext.Provider>
  );
}

export function useInsightsPage() {
  const ctx = useContext(InsightsPageContext);
  if (!ctx) {
    throw new Error("useInsightsPage must be used within InsightsPageProvider");
  }
  return ctx;
}

export function useInsightsSection(key) {
  const ctx = useContext(InsightsPageContext);
  if (!ctx) {
    return { section: null, loading: false, fromInsightsPage: false };
  }
  return {
    section: ctx.sections[key] ?? null,
    loading: ctx.loading,
    fromInsightsPage: true,
  };
}
